import { useCallback, useEffect, useState } from "react";
import { Film, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

const mb = (n) => (!n ? "—" : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

function fpsLabel(v) {
  if (!v) return null;
  return `${Math.round(v)}fps`;
}

function VideoRow({ v, onReoptimize, busy }) {
  const name = v.filename || v.url.split("/").pop();
  const fps = fpsLabel(v.optimized_fps);
  const isHighFps = v.optimized_fps >= 50;
  return (
    <li
      data-testid={`admin-video-row-${name}`}
      className="flex items-center gap-3 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-xl"
    >
      <video
        src={v.url}
        muted
        playsInline
        preload="metadata"
        className="w-16 h-10 rounded-md object-cover bg-stone-200 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-stone-800 truncate" title={name}>{name}</div>
        <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[10px] uppercase tracking-widest font-bold">
          {v.in_use && (
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800">In use</span>
          )}
          <span className="text-stone-500">{mb(v.optimized_size || v.size)}</span>
          {v.optimizing ? (
            <span data-testid={`admin-video-status-${name}`} className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1">
              <RefreshCw className="w-3 h-3 animate-spin" /> Optimizing…
            </span>
          ) : v.optimize_pending ? (
            <span data-testid={`admin-video-status-${name}`} className="px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800">Queued</span>
          ) : v.optimize_failed ? (
            <span data-testid={`admin-video-status-${name}`} className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-800">Failed — tap Re-optimize</span>
          ) : fps ? (
            <span
              data-testid={`admin-video-status-${name}`}
              className={`px-1.5 py-0.5 rounded-full ${isHighFps ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-700"}`}
            >
              {fps}
            </span>
          ) : (
            <span data-testid={`admin-video-status-${name}`} className="px-1.5 py-0.5 rounded-full bg-stone-200 text-stone-600">
              Older encode
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onReoptimize(v.url)}
        disabled={busy || v.optimizing || v.optimize_pending}
        data-testid={`admin-video-reoptimize-${name}`}
        className="text-[10px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full border-2 border-stone-800 text-stone-800 hover:bg-stone-800 hover:text-white disabled:opacity-40 disabled:cursor-wait shrink-0"
      >
        Re-optimize
      </button>
    </li>
  );
}

export function VideoLibraryCard({ token }) {
  const [videos, setVideos] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi(token).get("/admin/media/videos");
      setVideos(data.videos || []);
    } catch {
      setVideos((v) => v || []);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const anyOptimizing = (videos || []).some((v) => v.optimizing || v.optimize_pending);
  useEffect(() => {
    if (!anyOptimizing) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [anyOptimizing, load]);

  const reoptimize = async (url) => {
    const all = !url;
    if (all && !window.confirm("Re-encode every uploaded video at up to 60fps? Runs in the background — the storefront keeps working while it does.")) return;
    setBusy(true);
    try {
      const { data } = await adminApi(token).post("/admin/media/videos/reoptimize", url ? { url } : {});
      toast.success(all ? `Re-optimizing ${data.queued} video${data.queued === 1 ? "" : "s"} in the background` : "Re-optimizing — this can take a minute");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start re-optimize");
    } finally {
      setBusy(false);
    }
  };

  const unusedCount = (videos || []).filter((v) => !v.in_use).length;

  const purgeUnused = async () => {
    if (!window.confirm("Remove every uploaded video that isn't currently used as a background or banner? The built-in default clips are never affected.")) return;
    setBusy(true);
    try {
      const { data } = await adminApi(token).post("/admin/media/videos/purge-unused");
      toast.success(data.removed ? `Removed ${data.removed} unused video${data.removed === 1 ? "" : "s"}` : "Nothing to clean up — only in-use videos are stored");
      await load();
    } catch {
      toast.error("Clean-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-stone-200 rounded-2xl p-5 space-y-4" data-testid="admin-video-library">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-brand-red text-xl flex items-center gap-2">
            <Film className="w-5 h-5" /> Video Library
          </div>
          <div className="text-xs text-stone-500 mt-1 leading-snug">
            Only the clips in use as a background or banner are kept — swap one
            out and the old file is cleared automatically. Re-optimize gives
            older clips the smoother 60fps encode without re-uploading.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {unusedCount > 0 && (
            <button
              type="button"
              onClick={purgeUnused}
              disabled={busy}
              data-testid="admin-purge-unused-videos-btn"
              className="border-2 border-stone-300 text-stone-700 hover:border-red-600 hover:text-red-700 text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-full flex items-center gap-1 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" /> Clean up {unusedCount} unused
            </button>
          )}
          <button
            type="button"
            onClick={() => reoptimize(null)}
            disabled={busy || anyOptimizing || !videos?.length}
            data-testid="admin-reoptimize-videos-btn"
            className="bg-stone-800 hover:bg-stone-700 text-white text-xs uppercase tracking-widest font-bold px-3 py-2 rounded-full flex items-center gap-1 disabled:opacity-50 disabled:cursor-wait"
          >
            <Sparkles className="w-4 h-4" />
            {anyOptimizing ? "Optimizing…" : "Re-optimize all (60fps)"}
          </button>
        </div>
      </div>
      {videos === null ? (
        <div className="text-xs text-stone-400">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="text-xs text-stone-400 italic" data-testid="admin-video-library-empty">
          No videos uploaded yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {videos.map((v) => (
            <VideoRow key={v.url} v={v} onReoptimize={reoptimize} busy={busy} />
          ))}
        </ul>
      )}
    </section>
  );
}
